/**
 * Copia uma imagem (a partir de uma URL) para a área de transferência como
 * bitmap, pronta para colar (Ctrl+V) no CorelDRAW.
 *
 * O WebView2 embutido no Corel não abre novas abas/janelas, então "abrir no
 * navegador" não funciona ali. Este helper mantém tudo dentro do webview:
 * baixa os bytes da imagem, garante formato PNG (único aceito pelo clipboard
 * do Chromium/WebView2) e escreve no clipboard.
 *
 * Observações:
 * - Em produção o CDN (`/api/cdn/...`) é servido no mesmo domínio do frontend,
 *   então o `fetch` é same-origin e não esbarra em CORS.
 * - A reconversão para PNG usa `createImageBitmap` a partir do Blob já baixado,
 *   então o canvas não fica "tainted" (não passamos por um <img> cross-origin).
 */

function isImageClipboardSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard?.write === 'function' &&
    typeof ClipboardItem !== 'undefined' &&
    typeof createImageBitmap === 'function'
  );
}

async function reencodeToPng(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Não foi possível preparar a imagem para cópia.');
    }
    ctx.drawImage(bitmap, 0, 0);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((out) => {
        if (out) {
          resolve(out);
        } else {
          reject(new Error('Falha ao converter a imagem para PNG.'));
        }
      }, 'image/png');
    });
  } finally {
    bitmap.close();
  }
}

/**
 * Coloca um Blob de imagem (já em memória) no clipboard como PNG.
 * Reaproveita bytes já baixados (ex.: a miniatura da galeria), evitando um
 * segundo download.
 */
export async function copyImageBlobToClipboard(blob: Blob): Promise<void> {
  if (!isImageClipboardSupported()) {
    throw new Error('Cópia de imagem não é suportada neste ambiente.');
  }

  const pngBlob = blob.type === 'image/png' ? blob : await reencodeToPng(blob);
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
}

/**
 * Baixa a imagem da URL informada e a coloca no clipboard como PNG.
 * Lança erro (com mensagem amigável) em qualquer falha, para o chamador exibir.
 */
export async function copyImageToClipboard(url: string): Promise<void> {
  if (!url) {
    throw new Error('URL do layout ausente.');
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Falha ao baixar a imagem (HTTP ${response.status}).`);
  }

  await copyImageBlobToClipboard(await response.blob());
}
